import { Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, shareReplay, take } from 'rxjs/operators';
import { IChatFormFieldAction } from '@nimbus-global/features/chat-form/models/chat-form-resolver.model';
import { ILogger } from '@nimbus-library/features/logger/services/logger/i-logger';
import {
  CompanionMessageType,
  CompanionReferenceType,
  CompanionToolCallRecord,
  ICompanionReference,
} from '@nimbus-library/features/companion-chat/models/companion-conversation.model';
import { CompanionChatResolver } from './companion-chat-resolver';

interface ICompanionReferenceDto {
  kind?: unknown;
  id?: unknown;
  tenant_id?: string;
  organization_unit_id?: string;
}

@Injectable({ providedIn: 'root' })
export class CompanionChatReferenceService {
  public constructor(private readonly logger: ILogger) {}

  public resolveLinks(references: ICompanionReference[], resolver: CompanionChatResolver): Observable<IChatFormFieldAction[]> {
    if (!references.length || !resolver.resolveReferenceLink) {
      return of([]);
    }
    return forkJoin(
      references.map((reference) =>
        resolver.resolveReferenceLink(reference).pipe(
          take(1),
          catchError((error: unknown) => {
            this.logger.error(error);
            return of(null);
          }),
        ),
      ),
    ).pipe(
      map((links) => links.filter((link): link is IChatFormFieldAction => !!link)),
      shareReplay(1),
    );
  }

  public getReferencesChanged(
    toolCalls: CompanionToolCallRecord[],
    referenceTypeMapping: Readonly<Record<string, CompanionReferenceType>>,
  ): CompanionReferenceType[] {
    const calls = (toolCalls || []).filter(
      (call) =>
        !call.error &&
        call.metadata?.messageType !== CompanionMessageType.ConfirmationRequired &&
        call.metadata?.messageType !== CompanionMessageType.Form,
    );
    const entityKinds: CompanionReferenceType[] = [];
    for (const [toolName, kind] of Object.entries(referenceTypeMapping)) {
      if (calls.some((call) => call.toolName === toolName)) {
        entityKinds.push(kind);
      }
    }
    return [...new Set(entityKinds)];
  }

  public build(toolCalls: CompanionToolCallRecord[]): ICompanionReference[] {
    const calls = toolCalls || [];
    const references = calls
      .filter((call) => !call.error)
      .flatMap((call): ICompanionReference[] => {
        const raw = call.metadata?.entity_reference;
        if (!raw || Array.isArray(raw)) {
          return [];
        }
        const reference = raw as ICompanionReferenceDto;
        const kind = Object.values(CompanionReferenceType).find((value) => value === reference.kind);
        if (!kind || typeof reference.id !== 'string' || !reference.id) {
          return [];
        }
        return [
          {
            kind,
            id: reference.id,
            tenantId: reference.tenant_id || undefined,
            organizationUnitId: reference.organization_unit_id || undefined,
          },
        ];
      });
    return references.filter(
      (reference, index) => references.findIndex((other) => other.kind === reference.kind && other.id === reference.id) === index,
    );
  }
}
